import { FC, ReactNode } from "react";
import dayjs from "dayjs";
import Text from "apps/website/components/base/Text/Text";

import Button from "../../base/Button/Button";
import Icon from "../../base/Icon/Icon";
import Image, { IImage, IImageProps } from "../../base/Image/Image";
import Tag from "../../base/Tag/Tag";
import { legacySizeCollectionMap } from "../../base/Text/Text.map";
import Column from "../../layout/Column/Column";
import Spacer from "../../layout/Spacer/Spacer";
import Grid from "../../layout/Grid/Grid";
import SectionComparisonCardList, {
  ISectionComparisonCardListProps,
} from "../SectionComparisonCardList/SectionComparisonCardList";
import Sticker from "../../base/Sticker/Sticker";
import { STICKER_VARIANTS } from "../../base/Sticker/types";

interface ISectionOneClickPurchaseIcon {
  image: IImageProps;
  title: string;
}

export interface IOneClickPurchaseCard {
  title?: string;
  pricePrefix: string;
  price: string;
  salePrice: string;
  tag: string;
  subscriptionDetails?: string;
  addCtaText: string;
  removeCtaText: string;
  continueWithoutProductCtaText?: string;
  footer: string | ReactNode;
  images: IImage[],
  imageTagText?: string;
  isProductAdded?: boolean,
  onRemoveProduct?: () => void,
  onAddProduct?(): void,
  allowAddProduct?: boolean,
  deliveryDate?: string,
  body?: string | ReactNode,
  icons?: ISectionOneClickPurchaseIcon[];
  comparisonCards?: ISectionComparisonCardListProps;
  showFiftyOffSticker?: boolean;
}

const formatDeliveryDate = (dateString: string) => dayjs(dateString).format("ddd MMM D YYYY");

const CardStickers: FC<{ showFiftyOffSticker: boolean; imageTagText?: string }> = ({
  showFiftyOffSticker,
  imageTagText,
}) => (
  <>
    { showFiftyOffSticker && !imageTagText && (
      <div className="absolute -top-6 -left-6 z-10 -rotate-12">
        <Sticker variant={STICKER_VARIANTS.FIFTY_OFF} size="sm" />
      </div>
    ) }
    { imageTagText && (
      <Tag theme="pink" className="absolute top-4 left-4 z-10">{ imageTagText }</Tag>
    ) }
  </>
);

const PriceRow: FC<{ pricePrefix: string; price: string; salePrice: string; tag: string }> = ({
  pricePrefix,
  price,
  salePrice,
  tag,
}) => (
  <div className="flex justify-center items-center">
    <Text size={legacySizeCollectionMap.bodySm} align="center" className="flex justify-center items-center">
      <span data-testid="cardPricePrefix">
        { pricePrefix }
      </span>
      { price && (
        <>
                  &nbsp;{ salePrice ? (<s data-testid="cardPrice">{ price }</s>) : price }
        </>
      ) }
      { salePrice && (
        <>
                  &nbsp;<Text tag="span" display="subtitle" data-testid="cardSalePrice">{ salePrice }</Text>
        </>
      ) }
    </Text>
    { tag && (
      <Tag theme="pink" className="max-w-[75px] ml-2" whitespace="preWrap" size="small">{ tag }</Tag>
    ) }
  </div>
);

const DeliveryInfoGrid: FC<{ deliveryDate?: string; subscriptionDetails?: string }> = ({
  deliveryDate,
  subscriptionDetails,
}) => (
  <Grid>
    <>
      { deliveryDate &&
        <Column spans={6}>
          <Text size="2xs" className="flex items-center justify-center">
            First delivery:&nbsp;
            <Text tag="span" display="subtitle" size={legacySizeCollectionMap.titleXs} data-testid="cardDeliveryDate">
              { formatDeliveryDate(deliveryDate) }
            </Text>
          </Text>
        </Column>
      }
    </>
    <>
      { subscriptionDetails &&
        <Column spans={6}>
          <Text size="2xs" className="flex items-center justify-center">After that:&nbsp;
            <Text tag="span" display="subtitle" size={legacySizeCollectionMap.titleXs} data-testid="cardSubscriptionDetails">
              { subscriptionDetails }
            </Text>
          </Text>
        </Column>
      }
    </>
  </Grid>
);

interface IProductActionsProps {
  isProductAdded?: boolean;
  allowAddProduct?: boolean;
  deliveryDate?: string;
  addCtaText: string;
  removeCtaText: string;
  onAddProduct?: () => void;
  onRemoveProduct?: () => void;
}

const ProductActions: FC<IProductActionsProps> = ({
  isProductAdded,
  allowAddProduct,
  deliveryDate,
  addCtaText,
  removeCtaText,
  onAddProduct,
  onRemoveProduct,
}) => (
  <>
    <Column align="center" data-testid="cardAddProductWrapper">
      { isProductAdded ? (
        <div className="bg-green px-4 h-[50px] flex justify-center items-center space-x-4">
          <Icon icon="tick" size="small"/>
          <Text display="title">In your box</Text>
        </div>
      ) : <>{ allowAddProduct && (
        <Button onClick={onAddProduct} disabled={!deliveryDate} data-testid="cardAddCta">
          { addCtaText }
        </Button>
      ) }</> }
    </Column>
    <Column align="center">
      { isProductAdded && (
        <Button design="underline" onClick={onRemoveProduct}>
          { removeCtaText }
        </Button>
      ) }
    </Column>
  </>
);

export const SectionOneClickCard: FC<IOneClickPurchaseCard> = ({
  title,
  pricePrefix,
  price,
  salePrice,
  tag,
  subscriptionDetails,
  addCtaText,
  removeCtaText,
  footer,
  images,
  imageTagText,
  isProductAdded,
  onRemoveProduct,
  onAddProduct,
  allowAddProduct,
  deliveryDate,
  body,
  comparisonCards,
  showFiftyOffSticker = false,
}) => {
  return (
    <>
      <Grid>
        <Column data-testid="card" spans={{ md: 6, "2xl": 4 }} offset={{ md: 4, "2xl": 5 }} className="bg-white relative">
          <CardStickers showFiftyOffSticker={showFiftyOffSticker} imageTagText={imageTagText} />

          <div data-testid="SectionOneClickCard-control">
            <Image image={images[0].image} alt={images[0].alt} />
          </div>

          <div className="p-4">
            <Grid gap="lg">
              <Column justify={"between"}>
                { title &&
                  <Text tag="h2" display="title" size={legacySizeCollectionMap.titleSm} align="center" data-testid="cardTitle">
                    { title }
                  </Text>
                }
                <Spacer size="sm" />
                <Text tag="div" size="2xs" align="center" data-testid="cardFooter">
                  { body }
                </Text>
                <Spacer size="md" />
                <PriceRow pricePrefix={pricePrefix} price={price} salePrice={salePrice} tag={tag} />
                <DeliveryInfoGrid deliveryDate={deliveryDate} subscriptionDetails={subscriptionDetails} />
                <Spacer size="md" />
                <ProductActions
                  isProductAdded={isProductAdded}
                  allowAddProduct={allowAddProduct}
                  deliveryDate={deliveryDate}
                  addCtaText={addCtaText}
                  removeCtaText={removeCtaText}
                  onAddProduct={onAddProduct}
                  onRemoveProduct={onRemoveProduct}
                />
                <Text tag="div" size="2xs" align="center" data-testid="cardFooter">
                  { footer }
                </Text>
              </Column>
            </Grid>
          </div>
        </Column>
      </Grid>
      { comparisonCards?.cards && (
        <SectionComparisonCardList {...comparisonCards} />
      ) }
    </>
  );
};
